/**
 * wishlist.js — always loaded (defer), must stay tiny.
 *
 * Local-first by design: the cache in localStorage holds FULL item data
 * (title, image, price, handle), not just ids. Hearts, the header badge and
 * the popover all render instantly from it with zero network. The Cloudflare
 * Worker (GET /me) is a background reconcile — it keeps devices in sync and
 * survives cleared storage, but nothing the customer sees ever waits on it.
 *
 * Sources of item data, in order of arrival:
 *  - heart toggles capture full data from data-* attributes at click time
 *  - /me responses replace the cache wholesale (server is source of truth)
 *
 * Config injected from Liquid as window.WishlistConfig:
 *   { api, popoverUrl, pageUrl, customerId, icons, i18n:{...} }
 */
(function () {
  'use strict';
  var cfg = window.WishlistConfig;
  if (!cfg || !cfg.api) return;

  // Liquid's `t` filter HTML-escapes locale strings (' -> &#39;); decode
  // before showing them as text.
  function decodeEntities(s) {
    if (typeof s !== 'string' || s.indexOf('&') === -1) return s;
    var el = document.createElement('textarea');
    el.innerHTML = s;
    return el.value;
  }

  var LS_TOKEN = 'wl:token';
  var LS_CACHE = 'wl:cache';
  var LS_LINKED = 'wl:linked';

  /* ------------------------------------------------ cache (full items) */
  // Item shape: { v, p, handle, title, image, price, choose }
  // v/p are the variant/product ids as strings; choose is 1 when the product
  // needs options picking on the PDP rather than one-tap add.
  function loadCache() {
    try {
      var c = JSON.parse(localStorage.getItem(LS_CACHE) || 'null');
      // Older cache versions stored {v, p} only. Those entries are still
      // valid — rows render with what they have and the next /me fills them.
      if (c && Array.isArray(c.items)) return { items: c.items };
    } catch (e) { /* noop */ }
    return { items: [] };
  }
  var cache = loadCache();
  function saveCache() {
    try { localStorage.setItem(LS_CACHE, JSON.stringify(cache)); } catch (e) { /* noop */ }
  }
  function getToken() {
    try { return localStorage.getItem(LS_TOKEN); } catch (e) { return null; }
  }
  function setToken(t) {
    try { if (t) localStorage.setItem(LS_TOKEN, t); } catch (e) { /* noop */ }
  }
  function productIn(pid) {
    return cache.items.some(function (i) { return i.p === pid; });
  }
  function variantsOf(pid) {
    return cache.items.filter(function (i) { return i.p === pid; }).map(function (i) { return i.v; });
  }

  /* ------------------------------------------------ api */
  function api(path, opts) {
    opts = opts || {};
    var headers = opts.body ? { 'Content-Type': 'application/json' } : {};
    var t = getToken();
    if (t) headers['X-Wishlist-Token'] = t; // fallback when cookies are blocked
    return fetch(cfg.api + path, {
      method: opts.method || 'GET',
      credentials: 'include', // first-party cookie does the real auth
      headers: headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    }).then(function (res) {
      return res.json().then(function (data) {
        if (data && data.token) setToken(data.token); // mirror to localStorage
        if (!res.ok) { var e = new Error(data && data.error || res.status); e.data = data; e.status = res.status; throw e; }
        return data;
      });
    });
  }

  /* ------------------------------------------------ paint */
  var paintQueued = false;
  function paint() {
    if (paintQueued) return;
    paintQueued = true;
    requestAnimationFrame(function () {
      paintQueued = false;
      // Every write below is guarded on the value actually changing. paint() is
      // driven by a MutationObserver on document.body, so an unconditional write
      // makes it retrigger itself on the next frame and never settle.
      var hearts = document.querySelectorAll('[data-wishlist-toggle]');
      for (var i = 0; i < hearts.length; i++) {
        var el = hearts[i];
        var active = productIn(el.getAttribute('data-product-id'));
        var pressed = active ? 'true' : 'false';

        if (el.classList.contains('is-active') !== active) el.classList.toggle('is-active', active);
        if (el.getAttribute('aria-pressed') !== pressed) el.setAttribute('aria-pressed', pressed);
      }

      var badges = document.querySelectorAll('[data-wishlist-count]');
      var n = cache.items.length;
      var label = n > 99 ? '99+' : String(n);
      var empty = n === 0;
      for (var j = 0; j < badges.length; j++) {
        // `textContent =` replaces the text node even when the string is
        // identical, and that childList mutation was this loop's engine.
        if (badges[j].textContent !== label) badges[j].textContent = label;
        if (badges[j].hidden !== empty) badges[j].hidden = empty;
      }
    });
  }

  /* ------------------------------------------------ reconcile */
  function applyServerItems(items) {
    cache.items = (items || []).map(function (i) {
      return {
        v: String(i.variant_id),
        p: String(i.product_id),
        handle: i.handle || '',
        title: i.title || '',
        image: i.image || '',
        price: i.price || '',
        choose: i.choose ? 1 : 0,
      };
    });
    saveCache();
    paint();
  }

  function reconcile() {
    return api('/me').then(function (data) {
      applyServerItems(data.items);
      return data;
    }).catch(function () { return null; }); // offline / blocked: keep local cache
  }

  function maybeLink(me) {
    if (!cfg.customerId) return;
    var flag = null;
    try { flag = localStorage.getItem(LS_LINKED); } catch (e) { /* noop */ }
    var authed = !!(me && me.token);
    var needsLink = !authed || (authed && !me.linked) || flag !== String(cfg.customerId);
    if (!needsLink) return;
    api('/link', { method: 'POST', body: { customer_id: String(cfg.customerId) } })
      .then(function (res) {
        try { localStorage.setItem(LS_LINKED, String(cfg.customerId)); } catch (e) { /* noop */ }
        if (res && (res.restored || res.merged)) reconcile(); // list changed: refresh
      })
      .catch(function () { /* rate limited or offline — try again next page */ });
  }

  /* ------------------------------------------------ toggle */
  function heartData(el) {
    return {
      variant_id: el.getAttribute('data-variant-id') || '',
      product_id: el.getAttribute('data-product-id') || '',
      handle: el.getAttribute('data-handle') || '',
      title: el.getAttribute('data-title') || '',
      image: el.getAttribute('data-image') || '',
      price: el.getAttribute('data-price') || '',
      choose: el.getAttribute('data-choose') === '1' ? 1 : 0,
    };
  }

  function onToggle(el) {
    var d = heartData(el);
    if (!/^\d+$/.test(d.variant_id) || !/^\d+$/.test(d.product_id)) return;

    if (productIn(d.product_id)) {
      // Optimistic remove of every cached variant of this product.
      var vids = variantsOf(d.product_id);
      var removed = cache.items.filter(function (i) { return i.p === d.product_id; });
      cache.items = cache.items.filter(function (i) { return i.p !== d.product_id; });
      saveCache();
      paint();
      vids.forEach(function (v) {
        api('/items/' + v, { method: 'DELETE' }).catch(function () {
          // Revert with the full cached entries, not bare ids.
          removed.forEach(function (item) {
            if (item.v === v) cache.items.push(item);
          });
          saveCache();
          paint();
        });
      });
    } else {
      // Optimistic add — the heart's data-* attributes carry everything the
      // popover needs, so the row renders instantly with no server round trip.
      cache.items.unshift({
        v: d.variant_id,
        p: d.product_id,
        handle: d.handle,
        title: d.title,
        image: d.image,
        price: d.price,
        choose: d.choose,
      });
      saveCache();
      paint();
      revealHeaderHeart();
      api('/items', { method: 'POST', body: d }).catch(function (err) {
        cache.items = cache.items.filter(function (i) { return i.v !== d.variant_id; });
        saveCache();
        paint();
        if (err && err.status === 409) toast(decodeEntities(cfg.i18n.limit || 'Wishlist is full'));
      });
    }
  }

  /* ------------------------------------------------ popover open */
  var ctx = {
    cfg: cfg,
    api: api,
    getCount: function () { return cache.items.length; },
    // Full items in the popover's shape, straight from the local cache.
    getItems: function () {
      return cache.items.map(function (i) {
        return {
          variant_id: i.v,
          product_id: i.p,
          handle: i.handle || '',
          title: i.title || '',
          image: i.image || '',
          price: i.price || '',
          choose: i.choose ? 1 : 0,
        };
      });
    },
    // The popover hands /me results back through this so the cache, hearts
    // and badge stay in sync with the server.
    syncFromServer: applyServerItems,
    removeVariant: function (v, p) {
      var removed = cache.items.filter(function (i) { return i.v === v; });
      cache.items = cache.items.filter(function (i) { return i.v !== v; });
      saveCache();
      paint();
      return api('/items/' + v, { method: 'DELETE' }).catch(function () {
        removed.forEach(function (item) { cache.items.push(item); });
        saveCache();
        paint();
      });
    },
    repaint: paint,
  };

  var popoverModule = null;

  // The popover module is loaded eagerly by scripts.liquid as a normal module
  // script and registers itself on window.__wlPopover; this only has to wait
  // for that registration. It must NOT inject the script itself: a
  // dynamically-inserted script is fetched at low network priority, and on
  // busy pages that request stalled in Chrome's queue for minutes, which is
  // exactly the "click the heart, nothing happens" bug.
  //
  // The wait is capped at 4s. If the module still isn't there (eager tag
  // stripped, network dead), the caller's catch sends the customer to the
  // wishlist page instead of leaving a dead button.
  function loadPopover() {
    if (window.__wlPopover) return Promise.resolve(window.__wlPopover);
    if (popoverModule) return popoverModule;

    popoverModule = new Promise(function (resolve, reject) {
      var tries = 0;
      (function check() {
        if (window.__wlPopover) return resolve(window.__wlPopover);
        if (++tries > 80) {
          popoverModule = null; // allow a fresh wait on the next click
          return reject(new Error('popover module not available'));
        }
        setTimeout(check, 50);
      })();
    });

    return popoverModule;
  }

  function onOpen(el) {
    // The popover opens even when the cache is empty: it renders an empty
    // state that explains the hearts, and if the server turns out to have
    // items (cleared storage, new device), the background /me backfills them
    // into the already-open panel.
    loadPopover().then(function (m) {
      m.toggle(el, ctx);
    }).catch(function (err) {
      console.error('[wishlist] popover unavailable:', err);
      window.location.href = cfg.pageUrl;
    });
  }

  /* ------------------------------------------------ header heart attention */
  // After an add: if the sticky header has scrolled away (data-sticky-state
  // 'idle' -> opacity 0), bring it back the same way scrolling up does, then
  // pulse the header heart so the customer sees where their wishlist lives.
  function revealHeaderHeart() {
    var header = document.querySelector('header-component');
    if (header && header.dataset.stickyState === 'idle') {
      header.dataset.stickyState = 'active';
      header.dataset.scrollDirection = 'up';
    }
    // Small delay so the header fade-in and the pulse read as a sequence.
    setTimeout(function () {
      var hearts = document.querySelectorAll('header-component [data-wishlist-open]');
      for (var i = 0; i < hearts.length; i++) {
        (function (el) {
          el.classList.remove('wishlist-attn');
          void el.offsetWidth; // restart the animation if one is mid-flight
          el.classList.add('wishlist-attn');
          el.addEventListener('animationend', function () {
            el.classList.remove('wishlist-attn');
          }, { once: true });
        })(hearts[i]);
      }
    }, 200);
  }

  /* ------------------------------------------------ tiny toast (no module) */
  var toastEl = null, toastTimer = null;
  function toast(msg) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'wishlist-toast';
      toastEl.setAttribute('role', 'status');
      document.body.appendChild(toastEl);
    }
    toastEl.textContent = msg;
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { toastEl.classList.remove('is-visible'); }, 2200);
  }

  /* ------------------------------------------------ variant change (PDP + cards) */
  document.addEventListener('shopify:product:select', function (e) {
    if (!e.promise) return;
    var scope = (e.target && e.target.closest && e.target.closest('product-card, product-component')) || document;
    e.promise.then(function (result) {
      var variant = result && result.variant;
      if (!variant || !variant.id) return;
      var m = String(variant.id).match(/(\d+)\s*$/);
      if (!m) return;
      var vid = m[1];
      var hearts = scope.querySelectorAll('[data-wishlist-toggle]');
      for (var i = 0; i < hearts.length; i++) {
        var el = hearts[i];
        el.setAttribute('data-variant-id', vid);
        if (variant.price && variant.price.amount) {
          try {
            el.setAttribute('data-price', new Intl.NumberFormat(document.documentElement.lang || undefined, {
              style: 'currency', currency: variant.price.currencyCode,
            }).format(Number(variant.price.amount)));
          } catch (err) { /* keep captured price */ }
        }
      }
    }).catch(function () { /* aborted fetch */ });
  });

  /* ------------------------------------------------ wire up */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    var toggleEl = t.closest('[data-wishlist-toggle]');
    if (toggleEl) {
      e.preventDefault();
      e.stopPropagation();
      onToggle(toggleEl);
      return;
    }
    var openEl = t.closest('[data-wishlist-open]');
    if (openEl) {
      e.preventDefault();
      onOpen(openEl);
    }
  }, true);

  /*
   * Repaint when new cards arrive (sliders, pagination, quick add, morphs).
   * Only *added elements* count: paint() writes text and attributes itself, and
   * repainting in response to its own writes is what made this feed itself.
   *
   * WHY THIS IS NOT INSTALLED UNTIL AFTER LOAD
   * ------------------------------------------
   * A childList+subtree observer on document.body runs its callback for every
   * batch of nodes anything on the page adds, and this callback calls
   * querySelector() -- a fresh subtree scan -- once per added element. During
   * load that is by a wide margin the most expensive thing in this file: the
   * PDP finishes with 3,208 elements, most of them injected by scripts, so the
   * observer pays a subtree scan per node straight through the window that
   * decides LCP and TBT. Lighthouse, 21 Aug 2026, mobile PDP: 383ms of
   * main-thread time attributed to wishlist.js -- the parse of a 15KB file is
   * a rounding error next to that.
   *
   * Nothing needs the observer before load. Hearts and the badge present in
   * the initial HTML are handled by the unconditional paint() below, and the
   * cases the observer exists for -- quick-add, slider pagination, section
   * morphs -- cannot happen until the customer interacts.
   */
  function observeAdditions() {
    new MutationObserver(function (records) {
      for (var r = 0; r < records.length; r++) {
        var added = records[r].addedNodes;
        for (var a = 0; a < added.length; a++) {
          var node = added[a];
          if (node.nodeType !== 1) continue;
          if (
            node.matches('[data-wishlist-toggle], [data-wishlist-count]') ||
            node.querySelector('[data-wishlist-toggle], [data-wishlist-count]')
          ) {
            paint();
            return;
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /*
   * Background work, all of it off the load path.
   *
   * reconcile() is a network read that nothing on screen waits for -- the local
   * cache has already painted, and the file's own contract is that it never
   * does. maybeLink() only matters for a signed-in customer, and only on the
   * first page after they sign in. Firing both at parse time put a fetch, a
   * JSON parse, a full cache rewrite and a repaint inside the LCP window for
   * no visible benefit.
   *
   * The extra paint() covers the gap: anything that added a heart between the
   * first paint and this point gets picked up before the observer takes over.
   */
  var backgroundStarted = false;

  function startBackground() {
    if (backgroundStarted) return;
    backgroundStarted = true;
    paint();
    observeAdditions();
    reconcile().then(maybeLink);
  }

  function scheduleBackground() {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(startBackground, { timeout: 3000 });
    } else {
      setTimeout(startBackground, 200);
    }
  }

  paint(); // instant, no network -- hearts and badge correct on the first frame

  if (document.readyState === 'complete') {
    scheduleBackground();
  } else {
    window.addEventListener('load', scheduleBackground, { once: true });
  }

  /*
   * Safety net, not the mechanism. Tapping a heart already works before this
   * runs -- the click handler is bound at parse time and paints from the local
   * cache -- but pull the background work forward anyway rather than leave /me
   * waiting on an idle callback that a busy page keeps postponing.
   */
  ['pointerdown', 'keydown'].forEach(function (type) {
    document.addEventListener(type, startBackground, { once: true, capture: true, passive: true });
  });
})();
